import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  deleteContactGroup,
  deleteContactGroupUserPortal,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { ContactGroup } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';

interface DeleteContactGroupProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  userView?: boolean;
  contactGroup: ContactGroup;
  contactGroupList: ContactGroup[] | undefined;
  selectedLabel: string;
  setSelectedLabel: Dispatch<SetStateAction<string>>;
  changeLogRoles: string[];
}

export default function DeleteContactGroupDialog({
  open,
  handleClose,
  userEmail,
  userView,
  contactGroup,
  contactGroupList,
  selectedLabel,
  setSelectedLabel,
  changeLogRoles,
}: DeleteContactGroupProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [deleteAllContacts, setDeleteAllContacts] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteContactGroup(
        userEmail,
        contactGroup.resourceName!,
        changeLogMessage,
        deleteAllContacts
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: deleteAllContacts ? 'info' : 'success',
        message: deleteAllContacts
          ? 'Label deleted sucessfully and the task to delete all contacts has begun.'
          : 'Label deleted successfully!',
      });

      if (selectedLabel === contactGroup.resourceName)
        setSelectedLabel('contactGroups/all');
      const newList =
        contactGroupList?.filter(
          (group) => group.resourceName != contactGroup.resourceName
        ) || [];
      queryClient.setQueryData([UserKeys.Labels, userEmail], newList);
      setChangeLogMessage('');
      handleClose();
      setDeleteAllContacts(false);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting this label. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.personal_contacts
          .delete_contact_label,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.personalContacts,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: userPortalMutate, isPending: userPortalPending } =
    useMutation({
      mutationFn: () =>
        deleteContactGroupUserPortal(
          contactGroup.resourceName!,
          deleteAllContacts
        ),
      onSuccess: () => {
        setSnackbar({
          open: true,
          severity: deleteAllContacts ? 'info' : 'success',
          message: deleteAllContacts
            ? 'Label deleted sucessfully and the task to delete all contacts has begun.'
            : 'Label deleted successfully!',
        });

        if (selectedLabel === contactGroup.resourceName)
          setSelectedLabel('contactGroups/all');
        const newList =
          contactGroupList?.filter(
            (group) => group.resourceName != contactGroup.resourceName
          ) || [];
        queryClient.setQueryData([UserKeys.Labels, userEmail], newList);
        setChangeLogMessage('');
        handleClose();
        setDeleteAllContacts(false);
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage:
            'There was an issue deleting this label. Please refresh and try again.',
        });
      },
    });

  function handleSubmit() {
    if (
      !userView &&
      isAbleToSeeUIElement(changeLogRoles, UserRoles.DeleteContactGroups.value)
    ) {
      setOpenChangeLogDialog(true);
    } else if (userView) {
      userPortalMutate();
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Delete Label"
      dialogContent={
        <Box>
          <Typography pb={1}>
            Delete <strong>{contactGroup.formattedName}</strong> from{' '}
            <strong>{userEmail}'s</strong> labels?
          </Typography>
          <FormControlLabel
            sx={{ width: 'fit-content' }}
            control={
              <Checkbox
                checked={deleteAllContacts}
                onChange={() => setDeleteAllContacts(!deleteAllContacts)}
              />
            }
            label={<Typography>Delete all contacts in this label</Typography>}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete Label',
        action: handleSubmit,
        loading: isPending || userPortalPending,
        disabled: false,
        color: 'error',
      }}
    />
  );
}
